import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import { AlertComponent, NavBar } from "../../components";
import DataContext from "../../context/DataContext";
import PaginatorContext from "../../context/PaginatorContext";
import { useFetch, usePermissions } from "../../hooks";
import { ProductProps } from "../../interfaces/interfaces";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Box, Container, Fade, IconButton, Paper, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import { useNavigate } from "react-router-dom";
import { useDate } from "../../hooks/useDate";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../utils/dataGridStyles";

interface FetchResponse {
  success?: boolean;
  data: ProductProps[];
}

export const SalesPage = () => {
  const theme = useTheme();
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const matches = useMediaQuery("(min-width:600px)");
  const { data, error, isLoading, pagination } = useFetch<FetchResponse>(
    "sales-paginate",
    paginationModel,
  );
  const { isSuccess, isError, onResetValues } = useContext(DataContext);
  const navigate = useNavigate();
  const { getDateFromString } = useDate();
  const { canManageSales } = usePermissions();
  const [tableData, setTableData] = useState<ProductProps[]>([]);

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "orderDate",
      headerName: "Order Date",
      renderHeader: () => <strong>{"Order Date"}</strong>,
      sortable: true,
      width: 150,
      valueGetter: (params: GridValueGetterParams) =>
        getDateFromString(params.row?.orderDate),
    },
    {
      field: "client",
      headerName: "Client",
      renderHeader: () => <strong>{"Client"}</strong>,
      sortable: true,
      width: 200,
    },
    {
      field: "wellName",
      headerName: "Well",
      renderHeader: () => <strong>{"Well"}</strong>,
      sortable: true,
      width: 200,
    },
    {
      field: "deliveryContact",
      headerName: "Delivery Contact",
      renderHeader: () => <strong>{"Delivery Contact"}</strong>,
      sortable: true,
      width: 200,
    },
    {
      field: "salesmanContact",
      headerName: "Salesman Contact",
      renderHeader: () => <strong>{"Salesman Contact"}</strong>,
      sortable: true,
      width: 200,
    },
    {
      field: "action",
      headerName: "Actions",
      filterable: false,
      sortable: false,
      renderHeader: () => <strong>{"ACTIONS"}</strong>,
      width: 150,
      renderCell: (params: any) => {
        return (
          <div className="action-buttons">
            {canManageSales && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => navigate(`/sales/edit/${params.row?.id}`)}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canManageSales && (
              <Tooltip title="Duplicate">
                <IconButton
                  onClick={() => navigate(`/sales/${params.row?.id}`)}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Print">
              <IconButton
                onClick={() => navigate(`/sales/${params.row?.id}`)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  let content: JSX.Element | JSX.Element[];

  const buttons = canManageSales
    ? [
        {
          title: "New",
          action: () => {
            onResetValues();
            navigate("/sales/new/0");
          },
          icon: <AddBoxIcon />,
        },
      ]
    : [];
  return (
    <>
      <NavBar title="Sales Orders" buttons={buttons} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  slots={{ toolbar: GridToolbar }}
                  density={matches ? "standard" : "compact"}
                  sx={modernDataGridStyles(theme)}
                  rows={tableData}
                  columns={columns}
                  loading={isLoading}
                  rowCount={+pagination?.totalRecords}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={fetchPaginationModel}
                />
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
    </>
  );
};
